import React, { useState } from "react";
import { motion } from "framer-motion";
import { Pencil, Trash2, Check, X, Inbox, ChevronsUpDown, Lock, LockOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatEuro, formatPct, formatDateShort } from "@/lib/format";

const STATES = [
  { value: "Orçamento", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 border-slate-300 dark:border-slate-600" },
  { value: "Pedido", cls: "bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border-blue-300 dark:border-blue-800" },
  { value: "Adjudicado", cls: "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border-amber-300 dark:border-amber-800" },
  { value: "Realizado", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800" },
  { value: "Recusado", cls: "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border-rose-300 dark:border-rose-800" },
];

// Leftmost columns that can be frozen (locked) during horizontal scroll.
const PINNABLE = [
  { key: "estado", label: "Estado", width: 150 },
  { key: "data_evento", label: "Data", width: 96, sortable: true },
  { key: "cliente", label: "Cliente", width: 180, sortable: true },
  { key: "local", label: "Local", width: 160, sortable: true },
];

const SCROLL_COLS = [
  { key: "nome_projeto", label: "Projeto", sortable: true },
  { key: "numero_processo", label: "Nº Processo", sortable: true },
  { key: "valor_total", label: "Valor de Venda", num: true, sortable: true },
  { key: "custos", label: "Total de Custos", num: true, sortable: true },
  { key: "margem", label: "Margem", num: true, sortable: true },
];

function MarginBadge({ pct }) {
  const positive = (pct || 0) > 0;
  return (
    <Badge variant="outline" className={positive
      ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800 font-mono-num"
      : "bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-300 font-mono-num"}>
      {formatPct(pct)}
    </Badge>
  );
}

function StatusCell({ event, onSave }) {
  const current = event.estado || "Orçamento";
  const st = STATES.find((s) => s.value === current) || STATES[0];
  return (
    <Select value={current} onValueChange={(v) => onSave(event.id, { estado: v })}>
      <SelectTrigger className={`h-8 w-[132px] text-xs font-semibold border ${st.cls}`} data-testid={`estado-select-${event.id}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STATES.map((s) => (
          <SelectItem key={s.value} value={s.value} data-testid={`estado-option-${s.value}-${event.id}`}>{s.value}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function EditableTextCell({ event, field, onSave, format, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(event[field] || "");

  const save = () => {
    onSave(event.id, { [field]: val });
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 min-w-[120px]">
        <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder}
          className="h-8 text-xs" autoFocus data-testid={`inline-edit-${field}-${event.id}`}
          onKeyDown={(e) => e.key === "Enter" && save()} />
        <button onClick={save} className="p-1 text-emerald-600 hover:bg-muted rounded" data-testid={`inline-save-${field}-${event.id}`}>
          <Check className="h-4 w-4" />
        </button>
        <button onClick={() => { setEditing(false); setVal(event[field] || ""); }} className="p-1 text-muted-foreground hover:bg-muted rounded">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  const raw = event[field];
  return (
    <div className="group/cell cursor-pointer min-w-[60px] flex items-center gap-1.5"
      onClick={() => setEditing(true)} data-testid={`editable-cell-${field}-${event.id}`}>
      <span className={raw ? "" : "text-muted-foreground italic"}>
        {format ? format(raw) : (raw || placeholder || "—")}
      </span>
      <Pencil className="h-3 w-3 opacity-0 group-hover/cell:opacity-60 transition-opacity shrink-0" />
    </div>
  );
}

function CommercialCell({ event, slug, nameKey, valKey, pctKey, onSave }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(event[nameKey] || "");

  const save = () => {
    onSave(event.id, { [nameKey]: name });
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 min-w-[160px]">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome comercial"
          className="h-8 text-xs" autoFocus data-testid={`inline-edit-${slug}-${event.id}`}
          onKeyDown={(e) => e.key === "Enter" && save()} />
        <button onClick={save} className="p-1 text-emerald-600 hover:bg-muted rounded" data-testid={`inline-save-${slug}-${event.id}`}>
          <Check className="h-4 w-4" />
        </button>
        <button onClick={() => { setEditing(false); setName(event[nameKey] || ""); }} className="p-1 text-muted-foreground hover:bg-muted rounded">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="min-w-[140px] group/com cursor-pointer" onClick={() => setEditing(true)}
      data-testid={`commercial-cell-${slug}-${event.id}`}>
      <div className="flex items-center gap-1.5">
        <span className={`text-xs font-medium ${event[nameKey] ? "" : "text-muted-foreground italic"}`}>
          {event[nameKey] || "Definir nome..."}
        </span>
        <Pencil className="h-3 w-3 opacity-0 group-hover/com:opacity-60 transition-opacity" />
      </div>
      <div className="font-mono-num text-xs">
        <span className="font-semibold">{formatEuro(event[valKey])}</span>
        <span className="text-muted-foreground ml-1">({formatPct(event[pctKey])})</span>
      </div>
    </div>
  );
}

const TH = "px-4 py-3 text-left align-middle text-xs font-semibold uppercase tracking-wider bg-muted";
const TD = "px-4 py-3 align-middle";

export default function EventsTable({ events, loading, onSave, onDelete, sort, setSort }) {
  const [locked, setLocked] = useState({ estado: true, data_evento: true, cliente: true, local: true });

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };
  const toggleLock = (key) => setLocked((l) => ({ ...l, [key]: !l[key] }));

  // compute cumulative left offset for locked pinnable columns (in order)
  const offsets = {};
  let acc = 0;
  for (const col of PINNABLE) {
    if (locked[col.key]) { offsets[col.key] = acc; acc += col.width; }
    else offsets[col.key] = null;
  }

  const pinStyle = (col, header) => {
    if (offsets[col.key] === null) return { minWidth: col.width };
    return { position: "sticky", left: `${offsets[col.key]}px`, minWidth: col.width, width: col.width, zIndex: header ? 30 : 10 };
  };

  const SortIcon = ({ k }) => (
    <ChevronsUpDown className={`h-3 w-3 ${sort.key === k ? "opacity-90" : "opacity-40"}`} />
  );

  if (!loading && events.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-12 flex flex-col items-center justify-center text-center gap-3" data-testid="events-empty-state">
        <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center">
          <Inbox className="h-7 w-7 text-muted-foreground" />
        </div>
        <div>
          <p className="font-semibold">Ainda não existem eventos</p>
          <p className="text-sm text-muted-foreground mt-1">Importe um ficheiro .xlsx acima para começar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="overflow-auto max-h-[calc(100vh-240px)] rounded-xl" data-testid="events-table-scroll">
        <table className="w-full caption-bottom text-sm border-collapse">
          <thead className="sticky top-0 z-20">
            <tr>
              {PINNABLE.map((col) => {
                const isLocked = offsets[col.key] !== null;
                return (
                  <th key={col.key} style={pinStyle(col, true)}
                    className={`${TH} whitespace-nowrap ${isLocked ? "border-r border-border" : ""}`}>
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`inline-flex items-center gap-1 ${col.sortable ? "cursor-pointer select-none" : ""}`}
                        onClick={() => col.sortable && toggleSort(col.key)}>
                        {col.label}
                        {col.sortable && <SortIcon k={col.key} />}
                      </span>
                      <button onClick={() => toggleLock(col.key)} data-testid={`lock-toggle-${col.key}`}
                        title={isLocked ? "Desbloquear coluna" : "Bloquear coluna"}
                        className={`p-0.5 rounded hover:bg-background/60 transition-colors ${isLocked ? "text-primary" : "text-muted-foreground/50"}`}>
                        {isLocked ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </th>
                );
              })}
              {SCROLL_COLS.map((c) => (
                <th key={c.key} onClick={() => c.sortable && toggleSort(c.key)}
                  className={`${TH} whitespace-nowrap cursor-pointer select-none ${c.num ? "text-right" : ""}`}>
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    <SortIcon k={c.key} />
                  </span>
                </th>
              ))}
              <th className={`${TH} whitespace-nowrap`}>Margem %</th>
              <th className={`${TH} whitespace-nowrap text-right`}>Custos Indiretos</th>
              <th className={`${TH} whitespace-nowrap`}>Lucro Mundideias</th>
              <th className={`${TH} whitespace-nowrap`}>Lucro Comercial 1</th>
              <th className={`${TH} whitespace-nowrap`}>Lucro Comercial 2</th>
              <th className={`${TH} whitespace-nowrap`}>Lucro Comercial 3</th>
              <th className={`${TH} whitespace-nowrap text-right`}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => (
              <motion.tr key={ev.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="border-b border-border transition-colors hover:bg-muted/40 text-sm"
                data-testid={`events-table-row-${ev.numero_processo || ev.id}`}>
                <td style={pinStyle(PINNABLE[0], false)} className={`${TD} bg-card ${offsets.estado !== null ? "border-r border-border" : ""}`}>
                  <StatusCell event={ev} onSave={onSave} />
                </td>
                <td style={pinStyle(PINNABLE[1], false)} className={`${TD} bg-card whitespace-nowrap font-medium ${offsets.data_evento !== null ? "border-r border-border" : ""}`}>
                  <EditableTextCell event={ev} field="data_evento" onSave={onSave} format={formatDateShort} placeholder="Data" />
                </td>
                <td style={pinStyle(PINNABLE[2], false)} className={`${TD} bg-card whitespace-nowrap font-semibold ${offsets.cliente !== null ? "border-r border-border" : ""}`}>
                  <EditableTextCell event={ev} field="cliente" onSave={onSave} placeholder="Cliente" />
                </td>
                <td style={pinStyle(PINNABLE[3], false)} className={`${TD} bg-card whitespace-nowrap text-muted-foreground ${offsets.local !== null ? "border-r border-border" : ""}`}>
                  <EditableTextCell event={ev} field="local" onSave={onSave} placeholder="Local" />
                </td>
                <td className={`${TD} max-w-[220px]`}><EditableTextCell event={ev} field="nome_projeto" onSave={onSave} placeholder="Projeto" /></td>
                <td className={`${TD} whitespace-nowrap font-mono-num`}><EditableTextCell event={ev} field="numero_processo" onSave={onSave} placeholder="Nº Processo" /></td>
                <td className={`${TD} text-right font-mono-num font-semibold whitespace-nowrap`}>{formatEuro(ev.valor_total)}</td>
                <td className={`${TD} text-right font-mono-num text-rose-600 dark:text-rose-400 whitespace-nowrap`}>{formatEuro(ev.custos)}</td>
                <td className={`${TD} text-right font-mono-num text-emerald-600 dark:text-emerald-400 whitespace-nowrap`}>{formatEuro(ev.margem)}</td>
                <td className={TD}><MarginBadge pct={ev.margem_pct} /></td>
                <td className={`${TD} text-right`}>
                  <div className="font-mono-num text-xs">
                    <div className="font-semibold text-amber-600 dark:text-amber-400">{formatEuro(ev.custos_indiretos)}</div>
                    <div className="text-muted-foreground">({formatPct(ev.custos_indiretos_pct)})</div>
                  </div>
                </td>
                <td className={TD}><CommercialCell event={ev} slug="lucro-comercial" nameKey="lucro_comercial_nome" valKey="lucro_comercial_valor" pctKey="lucro_comercial_pct" onSave={onSave} /></td>
                <td className={TD}><CommercialCell event={ev} slug="comercial-1" nameKey="comercial_1_nome" valKey="comercial_1_valor" pctKey="comercial_1_pct" onSave={onSave} /></td>
                <td className={TD}><CommercialCell event={ev} slug="comercial-2" nameKey="comercial_2_nome" valKey="comercial_2_valor" pctKey="comercial_2_pct" onSave={onSave} /></td>
                <td className={TD}><CommercialCell event={ev} slug="comercial-3" nameKey="comercial_3_nome" valKey="comercial_3_valor" pctKey="comercial_3_pct" onSave={onSave} /></td>
                <td className={`${TD} text-right`}>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        data-testid={`delete-event-button-${ev.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminar evento?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Esta ação é permanente. O processo <strong>{ev.numero_processo}</strong> ({ev.cliente}) será removido.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onDelete(ev.id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          data-testid={`confirm-delete-button-${ev.id}`}>
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
